import { useMemo } from 'react';
import {
  FieldDefinition,
  FIELD_PATH_SEPARATOR,
} from '../../../../../../types/FieldDefinition';
import { FieldMappingEntry } from '../../../../../../types/DataSyncViewModel';
import { CG_BASE_CONTRACT_FIELDS } from '../../../../../../types/DataSyncItem';
import {
  buildTreeFromFieldDefinitions,
  enrichTreeWithMappings,
  FieldMappingTreeNode,
} from '../../../../../../utils/fieldMappingTreeBuilder';
import {
  buildValidationMap,
  calculateValidationSummary,
  ValidationResult,
  ValidationSummary,
} from '../../../../../../utils/fieldMappingValidator';
import {
  filterTreeData,
  FieldFilter,
} from '../../../../../../utils/fieldMappingSearch';

export interface UseFieldMappingsDataParams {
  sourceFields: FieldDefinition[];
  destinationFields: FieldDefinition[];
  viewModelFieldMappings: FieldMappingEntry[];
  fieldFilter: FieldFilter;
  searchQuery: string;
}

export interface UseFieldMappingsDataReturn {
  sourceFieldDisplayMap: Map<string, string>;
  sourceFieldValidations: Map<string, ValidationResult>;
  treeData: FieldMappingTreeNode[];
  filteredTreeData: FieldMappingTreeNode[];
  validationSummary: ValidationSummary;
}

/**
 * Custom hook for managing TreeFieldMappingsTable data transformations
 * Handles tree building, validation, filtering, and display name mapping
 */
export const useFieldMappingsData = ({
  sourceFields,
  destinationFields,
  viewModelFieldMappings,
  fieldFilter,
  searchQuery,
}: UseFieldMappingsDataParams): UseFieldMappingsDataReturn => {
  // Build a memoized map from source field paths to their display name paths
  // This avoids rebuilding the tree for every single cell
  const sourceFieldDisplayMap = useMemo(() => {
    const tree = buildTreeFromFieldDefinitions(sourceFields);
    const map = new Map<string, string>();

    // Recursively traverse tree and build display name paths
    const traverse = (
      nodes: FieldMappingTreeNode[],
      displayNamePath: string[] = []
    ) => {
      nodes.forEach((node) => {
        const currentDisplayPath = [...displayNamePath, node.displayName];
        map.set(
          node.path,
          currentDisplayPath.join(` ${FIELD_PATH_SEPARATOR} `)
        );

        if (node.children.length > 0) {
          traverse(node.children, currentDisplayPath);
        }
      });
    };

    traverse(tree);
    return map;
  }, [sourceFields]);

  // Build validation map for source field mappings
  const sourceFieldValidations = useMemo(() => {
    return buildValidationMap(
      viewModelFieldMappings,
      sourceFields,
      destinationFields
    );
  }, [viewModelFieldMappings, sourceFields, destinationFields]);

  // Build tree structure from destination fields and enrich with mappings
  const treeData = useMemo(() => {
    const tree = buildTreeFromFieldDefinitions(destinationFields);

    // Sort root level nodes: required fields first
    const sortedTree = [...tree].sort((a, b) => {
      // Only sort at root level (level 0)
      if (a.level === 0 && b.level === 0) {
        if (a.required && !b.required) { return -1; }
        if (!a.required && b.required) { return 1; }
        if (CG_BASE_CONTRACT_FIELDS.includes(a.name) && !CG_BASE_CONTRACT_FIELDS.includes(b.name)) { return -1; }
        if (!CG_BASE_CONTRACT_FIELDS.includes(a.name) && CG_BASE_CONTRACT_FIELDS.includes(b.name)) { return 1; }
      }
      return 0; // Preserve original order for same priority
    });

    return enrichTreeWithMappings(sortedTree, viewModelFieldMappings);
  }, [destinationFields, viewModelFieldMappings]);

  // Calculate validation summary
  const validationSummary = useMemo(() => {
    return calculateValidationSummary(sourceFieldValidations, treeData);
  }, [sourceFieldValidations, treeData]);

  // Filter tree based on search query and field filter
  const filteredTreeData = useMemo(() => {
    return filterTreeData(treeData, fieldFilter, searchQuery);
  }, [treeData, fieldFilter, searchQuery]);

  return {
    sourceFieldDisplayMap,
    sourceFieldValidations,
    treeData,
    filteredTreeData,
    validationSummary,
  };
};
